import { useId, useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import type { Locale } from '@/i18n/types';
import { rankAirlines, useAirlines } from '@/lib/api/airlines';
import { useAirportCities, useAirportSearch, useStations } from '@/lib/api/airports';
import { cx } from '@/lib/cx';
import { describeAccess, useAccessNames } from '@/lib/flightAccess';
import { cityOf } from '@/types/airport';
import type { AirportCity } from '@/types/airport';
import { codeList } from '@/types/flightAccess';
import type { FlightAccess, FlightAccessCard } from '@/types/flightAccess';
import { CodePicker } from './CodePicker';
import type { PickerOption } from './CodePicker';
import styles from './FlightAccess.module.css';

/**
 * The flight access of one account, as a form: "All flights" or "Only selected
 * flights", the airports and airlines that select them, and the sentence that says
 * what the two lists mean together.
 *
 * Controlled, and used twice — inside the create form, where it is sent right after
 * the account is made, and in the edit panel's own section, where it has its own
 * Save. The sentence is the same one the user later sees on their board, so the
 * administrator reads, while choosing, exactly what they are granting.
 */

type Translate = (key: Key, values?: Record<string, string | number>) => string;

/** What the form holds. The lists are kept while "All flights" is chosen, so switching back restores them. */
export interface AccessDraft {
  restricted: boolean;
  airports: string[];
  airlines: string[];
}

/** The three fields the server names in a `DataError.path`. */
export type AccessField = 'restricted' | 'airports' | 'airlines';
export type AccessErrors = Partial<Record<AccessField, string>>;

export const ACCESS_FIELD_OF: Readonly<Record<string, AccessField>> = {
  restricted: 'restricted',
  airports: 'airports',
  airlines: 'airlines',
};

export const draftOfAccess = (access: FlightAccess | null): AccessDraft => ({
  restricted: access?.restricted ?? false,
  airports: access?.airports ?? [],
  airlines: access?.airlines ?? [],
});

/**
 * The body to send.
 *
 * "All flights" goes with empty lists whatever the form still holds: the server
 * clears them anyway, and sending codes beside `restricted: false` would describe a
 * selection that does not apply.
 */
export const cardOfDraft = (draft: AccessDraft): FlightAccessCard =>
  draft.restricted
    ? { restricted: true, airports: codeList(draft.airports), airlines: codeList(draft.airlines) }
    : { restricted: false, airports: [], airlines: [] };

/** Whether saving this draft would change the stored access. */
export const isSameAccess = (draft: AccessDraft, access: FlightAccess): boolean => {
  const card = cardOfDraft(draft);
  const same = (left: readonly string[], right: readonly string[]) =>
    left.length === right.length && left.every((code, index) => code === right[index]);

  return (
    card.restricted === access.restricted &&
    same(card.airports, codeList(access.airports)) &&
    same(card.airlines, codeList(access.airlines))
  );
};

/**
 * The one rule settled before the round trip: a restriction needs at least one code.
 *
 * Mirrors the server's own refusal, and lands on the same field it names. Every code
 * here came from a dictionary, so the other refusal — a code the dictionary does not
 * have — is left to the server, which is the judge of what its dictionary holds.
 */
export const accessProblems = (draft: AccessDraft, t: Translate): AccessErrors => {
  const card = cardOfDraft(draft);
  return card.restricted && card.airports.length === 0 && card.airlines.length === 0
    ? { airports: t('users.access.needOne') }
    : {};
};

/**
 * The messages still true after one field changed.
 *
 * The changed field's own message goes — it was about a value that is no longer there.
 * So does "at least one airport or airline" once either list has a code: it stands
 * under Airports, but an airline answers it just as well, and leaving it there would
 * read as the airline having been refused.
 */
export const errorsAfterChange = (
  errors: AccessErrors,
  field: AccessField,
  next: AccessDraft,
  t: Translate,
): AccessErrors => {
  const remaining: AccessErrors = { ...errors };
  delete remaining[field];

  if (remaining.airports === t('users.access.needOne') && Object.keys(accessProblems(next, t)).length === 0) {
    delete remaining.airports;
  }

  return remaining;
};

interface FlightAccessEditorProps {
  value: AccessDraft;
  /** Reports the field that changed, so the caller can clear its message. */
  onChange: (next: AccessDraft, field: AccessField) => void;
  errors: AccessErrors;
  disabled?: boolean | undefined;
}

export const FlightAccessEditor = ({ value, onChange, errors, disabled = false }: FlightAccessEditorProps) => {
  const { t } = useTranslation();
  const group = useId();
  const names = useAccessNames(value.airports);

  const set = <K extends AccessField>(field: K, next: AccessDraft[K]) =>
    onChange({ ...value, [field]: next }, field);

  return (
    <div className={styles.editor}>
      <div className={styles.choices} role="radiogroup" aria-label={t('users.access.title')}>
        <Choice
          name={group}
          checked={!value.restricted}
          onSelect={() => set('restricted', false)}
          title={t('users.access.all')}
          hint={t('users.access.allHint')}
          disabled={disabled}
        />
        <Choice
          name={group}
          checked={value.restricted}
          onSelect={() => set('restricted', true)}
          title={t('users.access.selected')}
          hint={t('users.access.selectedHint')}
          disabled={disabled}
        />
      </div>

      {errors.restricted && <p className={styles.fieldError}>{errors.restricted}</p>}

      {value.restricted && (
        <div className={styles.pickers}>
          <AirportPicker
            values={value.airports}
            onChange={(airports) => set('airports', airports)}
            error={errors.airports}
            disabled={disabled}
          />
          <AirlinePicker
            values={value.airlines}
            onChange={(airlines) => set('airlines', airlines)}
            error={errors.airlines}
            disabled={disabled}
          />
        </div>
      )}

      {/* What the choice means, in the words the user will read on their board.
          Announced politely: it changes with every pick, and is a confirmation, not
          an interruption. */}
      <p
        className={cx(styles.result, value.restricted && styles.resultRestricted)}
        aria-live="polite"
      >
        <span className={styles.resultLabel}>{t('users.access.sees')}</span>
        {describeAccess(value, t, names)}
      </p>
    </div>
  );
};

/** One of the two choices: a radio with its consequence written under it. */
const Choice = ({
  name,
  checked,
  onSelect,
  title,
  hint,
  disabled,
}: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  title: string;
  hint: string;
  disabled: boolean;
}) => (
  <label className={cx(styles.choice, checked && styles.choiceOn, disabled && styles.choiceDisabled)}>
    <input
      type="radio"
      className={styles.radio}
      name={name}
      checked={checked}
      onChange={onSelect}
      disabled={disabled}
    />
    <span className={styles.choiceText}>
      <span className={styles.choiceTitle}>{title}</span>
      <span className={styles.choiceHint}>{hint}</span>
    </span>
  </label>
);

/**
 * How many options are built at once — a DOM budget, not a readability one. What is
 * cut off is stated, so a short list is never mistaken for the whole one.
 */
const MAX_OPTIONS = 100;

/** The switch to asking the server, as on the board: one letter is answered locally. */
const REMOTE_FROM = 2;

/**
 * Airports: the operation's stations to start with, the whole dictionary once two
 * characters are typed — the same two sources the board's route filters use.
 */
const AirportPicker = ({
  values,
  onChange,
  error,
  disabled,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  error: string | undefined;
  disabled: boolean;
}) => {
  const { t, locale } = useTranslation();
  const [query, setQuery] = useState('');
  const { stations, isLoading: stationsLoading } = useStations();
  const airportOf = useAirportCities(values);

  const trimmed = query.trim();
  const isRemote = trimmed.length >= REMOTE_FROM;
  const remote = useAirportSearch(isRemote ? trimmed : '');

  const found = useMemo(
    () => airportOptions(isRemote ? remote.results : stations, isRemote ? '' : trimmed, locale, !isRemote),
    [isRemote, remote.results, stations, trimmed, locale],
  );

  const options = found.slice(0, MAX_OPTIONS);
  const hidden = found.length - options.length;

  return (
    <CodePicker
      label={t('users.access.airports')}
      // hint={t('users.access.airportsHint')}
      error={error}
      values={values}
      onChange={onChange}
      query={query}
      onQueryChange={setQuery}
      options={options}
      isLoading={isRemote ? remote.isSearching : stationsLoading}
      emptyLabel={remote.hasFailed ? t('schedule.airportsFailed') : t('schedule.noMatches')}
      footnote={
        remote.isSearching
          ? t('schedule.searching')
          : hidden > 0
            ? t('schedule.moreMatches', { count: hidden })
            : null
      }
      nameOf={(code) => {
        const airport = airportOf(code);
        return airport ? cityOf(airport, locale) : '';
      }}
      placeholder={t('users.access.airportSearch')}
      disabled={disabled}
    />
  );
};

/**
 * Airports as options: `NQZ — Astana`.
 *
 * `filter` narrows the LOCAL list only; a server search has done its own matching.
 * The stations are ordered by city, the way the board's station list reads; a search
 * keeps the server's order, which is its idea of relevance.
 */
const airportOptions = (
  airports: readonly AirportCity[],
  filter: string,
  locale: Locale,
  byCity: boolean,
): PickerOption[] => {
  const needle = filter.trim().toUpperCase();

  const options = airports.flatMap((airport) => {
    const code = (airport.airportIataCode ?? '').trim().toUpperCase();
    if (code === '') return [];

    const city = cityOf(airport, locale);
    if (needle !== '' && !code.startsWith(needle) && !city.toUpperCase().includes(needle)) return [];

    return [{ code, label: city, key: airport.id }];
  });

  return byCity ? options.sort((left, right) => left.label.localeCompare(right.label, locale)) : options;
};

/** An IATA airline designator: two letters or digits. */
const AIRLINE_CODE = /^[A-Z0-9]{2}$/;

/**
 * Airlines, from the working list — the carriers this operation flies with, already
 * in the browser for the board.
 *
 * The SERVER checks a code against the whole dictionary, which is larger than the
 * working list. So a two-character code the list does not have is still offered, as
 * itself and marked as off the list: refusing it here would make the browser stricter
 * than the rule, and if the code is not in the dictionary either, the server says so
 * under this field.
 */
const AirlinePicker = ({
  values,
  onChange,
  error,
  disabled,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  error: string | undefined;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { airlines, isLoading, hasFailed } = useAirlines();

  const titles = useMemo(() => {
    const byCode = new Map<string, string>();
    for (const airline of airlines) {
      const code = (airline.iataCode ?? '').trim().toUpperCase();
      if (code !== '' && airline.title) byCode.set(code, airline.title);
    }
    return byCode;
  }, [airlines]);

  const found = useMemo<PickerOption[]>(
    () =>
      rankAirlines(airlines, query).map((airline) => ({
        code: (airline.iataCode ?? '').trim().toUpperCase(),
        label: airline.title ?? '',
        key: airline.id,
      })),
    [airlines, query],
  );

  const typed = query.trim().toUpperCase();
  const offerTyped = AIRLINE_CODE.test(typed) && !titles.has(typed);

  const options: PickerOption[] = [
    ...(offerTyped ? [{ code: typed, label: '', note: t('users.access.offList'), key: `typed:${typed}` }] : []),
    ...found.slice(0, MAX_OPTIONS),
  ];
  const hidden = found.length - Math.min(found.length, MAX_OPTIONS);

  return (
    <CodePicker
      label={t('users.access.airlines')}
      // hint={t('users.access.airlinesHint')}
      error={error}
      values={values}
      onChange={onChange}
      query={query}
      onQueryChange={setQuery}
      options={options}
      isLoading={isLoading}
      emptyLabel={hasFailed ? t('schedule.airlinesFailed') : t('schedule.noMatches')}
      footnote={hidden > 0 ? t('schedule.moreMatches', { count: hidden }) : null}
      nameOf={(code) => titles.get(code) ?? ''}
      placeholder={t('users.access.airlineSearch')}
      disabled={disabled}
    />
  );
};
